import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PromptVoiceDesign from "./PromptVoiceDesign";
import PromptVoicePreview from "./PromptVoicePreview";
import PromptVoiceInformation from "./PromptVoiceInformation";
import CloneVoiceStatusModal from "../../../components/modals/CloneVoiceStatusModal";
import {
    getPromptVoiceStats,
    designPromptVoice,
    savePromptVoice,
    PromptVoiceStats,
    PromptVoicePreview as PreviewType,
} from "../../../api/promptVoice";
import styles from "./PromptVoice.module.css";

const PromptVoice: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [currentStep, setCurrentStep] = useState<"design" | "preview" | "information">("design");
    const [stats, setStats] = useState<PromptVoiceStats | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [previews, setPreviews] = useState<PreviewType[]>([]);
    const [selectedPreview, setSelectedPreview] = useState<PreviewType | null>(null);
    const [voiceDescription, setVoiceDescription] = useState("");
    const [statusModal, setStatusModal] = useState<{
        isOpen: boolean;
        status: "waiting" | "success" | "error";
        title: string;
        subtitle: string;
    }>({
        isOpen: false,
        status: "waiting",
        title: "",
        subtitle: "",
    });

    // Fetch stats on mount
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const statsData = await getPromptVoiceStats(token);
                setStats(statsData);
            } catch (error) {
                console.error("Error fetching prompt voice stats:", error);
            } finally {
                setIsLoadingStats(false);
            }
        };

        fetchStats();
    }, []);

    const handleGenerate = async (params: {
        voiceDescription: string;
        previewText: string;
        autoGenerateText: boolean;
        loudness: number;
        guidanceScale: number;
    }) => {
        if (stats && !stats.canCreateMore) {
            return;
        }

        setIsGenerating(true);
        setVoiceDescription(params.voiceDescription);

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                alert(t("pleaseLogin"));
                return;
            }

            const response = await designPromptVoice({
                token,
                voiceDescription: params.voiceDescription,
                previewText: params.previewText,
                autoGenerateText: params.autoGenerateText,
                loudness: params.loudness,
                guidanceScale: params.guidanceScale,
            });

            setPreviews(response.previews);
            setCurrentStep("preview");
        } catch (error: any) {
            console.error("Error generating previews:", error);

            const errorMessage = error?.details?.message || error?.message || "";
            if (errorMessage === "prompt-voice-limit-reached") {
                alert(t("promptVoiceLimitReached"));
                // Refresh stats
                try {
                    const token = localStorage.getItem("token");
                    if (token) {
                        const updatedStats = await getPromptVoiceStats(token);
                        setStats(updatedStats);
                    }
                } catch (e) {
                    console.error("Error refreshing stats:", e);
                }
            } else {
                alert(errorMessage || t("errorGeneratingPreviews"));
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSelectPreview = (preview: PreviewType) => {
        setSelectedPreview(preview);
        setCurrentStep("information");
    };

    const handleBackToDesign = () => {
        setPreviews([]);
        setSelectedPreview(null);
        setCurrentStep("design");
    };

    const handleBackToPreview = () => {
        setSelectedPreview(null);
        setCurrentStep("preview");
    };

    const handleSaveVoice = async (
        name: string,
        description: string,
        gender?: string,
        accent?: string,
        age?: string,
        language?: string,
    ) => {
        if (!selectedPreview) {
            alert(t("pleaseSelectPreview"));
            return;
        }

        setIsSaving(true);
        setStatusModal({
            isOpen: true,
            status: "waiting",
            title: t("savingVoice"),
            subtitle: t("savingVoiceSubtitle"),
        });

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                alert(t("pleaseLogin"));
                setIsSaving(false);
                return;
            }

            const response = await savePromptVoice({
                token,
                externalVoiceId: selectedPreview.externalVoiceId,
                name,
                description,
                gender,
                accent,
                age,
                language,
            });

            setStatusModal({
                isOpen: true,
                status: "success",
                title: t("voiceSavedTitle"),
                subtitle: t("voiceSavedSubtitle", { name: response.promptVoice.name }),
            });

            // Refresh stats
            try {
                const updatedStats = await getPromptVoiceStats(token);
                setStats(updatedStats);
            } catch (e) {
                console.error("Error refreshing stats:", e);
            }

            // Reset wizard
            setPreviews([]);
            setSelectedPreview(null);
            setVoiceDescription("");
            setCurrentStep("design");
        } catch (error: any) {
            console.error("Error saving voice:", error);

            const errorMessage = error?.details?.message || error?.message || "";
            if (errorMessage === "prompt-voice-limit-reached") {
                setStatusModal({
                    isOpen: true,
                    status: "error",
                    title: t("voiceSaveErrorTitle"),
                    subtitle: t("promptVoiceLimitReached"),
                });
            } else {
                setStatusModal({
                    isOpen: true,
                    status: "error",
                    title: t("voiceSaveErrorTitle"),
                    subtitle: errorMessage || t("voiceSaveErrorSubtitle"),
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    const getStepLabel = (step: "design" | "preview" | "information") => {
        switch (step) {
            case "design":
                return t("describeVoice");
            case "preview":
                return t("selectPreview");
            case "information":
                return t("voiceInformation");
        }
    };

    return (
        <>
            <div className={styles.cloneVoiceContainer}>
                <div className={styles.cloneVoiceHeader}>
                    <div className={styles.headerTop}>
                        <h2>{t("designAVoice")}</h2>
                        {!isLoadingStats && stats && (
                            <div className={styles.voiceCloneStats}>
                                <span className={styles.statsLabel}>{t("promptVoicesRemaining")}:</span>
                                <span
                                    className={`${styles.statsValue} ${stats.remaining === 0 ? styles.limitReached : ""}`}
                                >
                                    {stats.remaining} / {stats.limit}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Steps Indicator */}
                    <div className={styles.stepsIndicator}>
                        <div
                            className={`${styles.step} ${
                                currentStep === "design" ? styles.activeStep : styles.completedStep
                            }`}
                        >
                            <span className={styles.dot} />
                            <span className={styles.stepLabel}>{getStepLabel("design")}</span>
                        </div>
                        <div className={styles.stepLine}></div>
                        <div
                            className={`${styles.step} ${
                                currentStep === "preview"
                                    ? styles.activeStep
                                    : currentStep === "information"
                                      ? styles.completedStep
                                      : ""
                            }`}
                        >
                            <span className={styles.dot} />
                            <span className={styles.stepLabel}>{getStepLabel("preview")}</span>
                        </div>
                        <div className={styles.stepLine}></div>
                        <div className={`${styles.step} ${currentStep === "information" ? styles.activeStep : ""}`}>
                            <span className={styles.dot} />
                            <span className={styles.stepLabel}>{getStepLabel("information")}</span>
                        </div>
                    </div>
                </div>

                {/* Limit Reached Banner */}
                {stats && !stats.canCreateMore && (
                    <div className={styles.limitReachedBanner}>
                        <p>{t("promptVoiceLimitReached")}</p>
                        <p>{t("maximumPromptVoicesReached", { limit: stats.limit })}</p>
                    </div>
                )}

                {/* Step Content */}
                {currentStep === "design" && (
                    <PromptVoiceDesign
                        onGenerate={handleGenerate}
                        isGenerating={isGenerating}
                        disabled={stats ? !stats.canCreateMore : false}
                    />
                )}

                {currentStep === "preview" && previews.length > 0 && (
                    <PromptVoicePreview
                        previews={previews}
                        onSelect={handleSelectPreview}
                        onBack={handleBackToDesign}
                    />
                )}

                {currentStep === "information" && selectedPreview && (
                    <PromptVoiceInformation
                        onBack={handleBackToPreview}
                        onSave={handleSaveVoice}
                        isSaving={isSaving}
                        defaultDescription={voiceDescription}
                    />
                )}
            </div>

            <CloneVoiceStatusModal
                isOpen={statusModal.isOpen}
                status={statusModal.status}
                title={statusModal.title}
                subtitle={statusModal.subtitle}
                onClose={() =>
                    setStatusModal((prev) => ({
                        ...prev,
                        isOpen: false,
                    }))
                }
            />
        </>
    );
};

export default PromptVoice;
